import React from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'

import No from '../components/Misc/No'
import NoAccess from '../components/Misc/NoAccess'
import ErrorBoundary from '../components/Misc/ErrorBoundary'
import { isNonProductionEnvironment } from '../utils/environment'

import {
	Activities,
	ActivitySetup,
	Add,
	AddonAvailability,
	AvailablePlaygroups,
	BarAndPark,
	BarParkSettings,
	BodyCheck,
	Booking,
	Cancellation,
	Capacity,
	ClientAppointments,
	ClientContacts,
	ClientCreate,
	ClientHousehold,
	ClientHouseholdData,
	ClientPayments,
	ClientPets,
	Clients,
	CreateAppointment,
	AppUpdate,
	Exports,
	FacilitiesDashboard as NoFacility,
	FacilityBasicInfo,
	FacilityMemberships,
	FacilityHolidays,
	FacilityMarketing,
	GroomingAssignment,
	GroomingSchedule,
	GroomingCalendar,
	Kennels,
	Market,
	Markets,
	Membership,
	NightKennels,
	Pet,
	PetCareSetup,
	Playgroup,
	Playgroups,
	Promotions,
	RunPayments,
	Schedule,
	ServicesAvailability,
	StaffCreate,
	StaffNotes,
	Team,
	TeamMember,
	Unassigned,
	Visit,
	Mfa,
	TrainingBooking,
	TrainingCourseTypes,
	TrainingLocations,
	TrainingCancellation,
	TrainingSchedule,
	TrainingClass,
	TrainingCalendar,
	TrainingEnrollment,
	PlayYardSchedule,
	Dashboard
} from '../pages'

const VisitPageWithErrorBoundary = (props) => (
	<ErrorBoundary>
		<Visit {...props} />
	</ErrorBoundary>
)

const PrivateRoute = ({ component: Component, authed, roles, requiredRoles, superUserOnly, ...rest }) => {
	return (
		<Route
			{...rest}
			render={(props) => {
				// let pathname = props?.location?.pathname && !props?.location?.pathname.includes('login') ? `/login?redirect=${props.location.pathname}` : '/login'

				if (!authed) return <Redirect to={{ pathname: '/login', state: { from: props.location } }} />
				if (superUserOnly) return roles.includes('Super User') ? <Component {...props} /> : <No />
				if (!requiredRoles || !requiredRoles.length || roles.includes('Back Office User') || roles.includes('Super User'))
					return <Component {...props} />
				if (requiredRoles.some((r) => !!roles.find((role) => role === r))) return <Component {...props} />
				return <No />
			}}
		/>
	)
}

export const privateRoutes = (authed, roles) => {
	if (authed && (!roles || !roles.length)) {
		return (
			<Switch>
				<NoAccess />
			</Switch>
		)
	}

	return (
		<Switch>
			<PrivateRoute authed={authed} roles={roles} path="/mfa" component={Mfa} />
			<PrivateRoute authed={authed} roles={roles} path="/" exact component={NoFacility} />
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Support User', 'PCA Supervisor User']}
				path="/facilities/:facilityId/schedule"
				component={Schedule}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				path="/facilities/:facilityId/groomingHome"
				requiredRoles={['Support User', 'PCA Supervisor User', 'Groomer']}
				component={GroomingSchedule}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				path="/facilities/:facilityId/groomingCalendar"
				requiredRoles={['Support User', 'PCA Supervisor User', 'Groomer']}
				component={GroomingCalendar}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				path="/facilities/:facilityId/groomingAssignment"
				requiredRoles={['Support User', 'PCA Supervisor User', 'Groomer']}
				component={GroomingAssignment}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				path="/facilities/:facilityId/appointment/create"
				requiredRoles={['Support User', 'PCA Supervisor User', 'Groomer']}
				component={CreateAppointment}
			/>
			{/*<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/globalAdmin/exports"
				component={Exports}
			/>*/}
			<PrivateRoute
				authed={authed}
				roles={roles}
				path="/globalAdmin/markets"
				requiredRoles={['Back Office User']}
				exact
				component={Markets}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				path="/globalAdmin/markets/:marketId/home"
				requiredRoles={['Back Office User']}
				component={Market}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				path="/globalAdmin/promos"
				superUserOnly
				exact
				component={Promotions}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				path="/globalAdmin/appUpdate"
				requiredRoles={['Back Office User']}
				exact
				component={AppUpdate}
			/>
			{isNonProductionEnvironment() && (
				<PrivateRoute
					authed={authed}
					roles={roles}
					path="/globalAdmin/run-payments"
					requiredRoles={['Back Office User']}
					exact
					component={RunPayments}
				/>
			)}
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Support User', 'PCA Supervisor User', 'Groomer']}
				exact
				path="/clients"
				component={Clients}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Support User', 'PCA Supervisor User']}
				exact
				path="/clients/create"
				component={ClientCreate}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Support User', 'PCA Supervisor User', 'Groomer']}
				path="/clients/:clientId/appointments/:visitId"
				component={VisitPageWithErrorBoundary}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Support User', 'PCA Supervisor User', 'Groomer']}
				exact
				path="/clients/:clientId/appointments"
				component={ClientAppointments}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Support User', 'PCA Supervisor User', 'Groomer']}
				exact
				path="/clients/:clientId/contacts"
				component={ClientContacts}
			/>
			<PrivateRoute authed={authed} roles={roles} exact path="/clients/:clientId/pets/add" component={Add} />
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Support User', 'PCA Supervisor User', 'Groomer']}
				exact
				path="/clients/:clientId/pets/:petId"
				component={Pet}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Support User', 'PCA Supervisor User', 'Groomer']}
				exact
				path="/clients/:clientId/pets"
				component={ClientPets}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Support User', 'PCA Supervisor User', 'Groomer']}
				path="/clients/:clientId/appointments/:visitId/pet/:petId/bodycheck"
				component={BodyCheck}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Support User', 'PCA Supervisor User', 'Groomer']}
				exact
				path="/clients/:clientId/household"
				component={ClientHousehold}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Support User', 'PCA Supervisor User']}
				exact
				path="/clients/:clientId/household/data"
				component={ClientHouseholdData}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Support User', 'PCA Supervisor User']}
				exact
				path="/clients/:clientId/payments"
				component={ClientPayments}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Support User', 'PCA Supervisor User']}
				exact
				path="/clients/:clientId/membership"
				component={Membership}
			/>
			<PrivateRoute
				authed={authed}
				requiredRoles={['Support User', 'PCA Supervisor User']}
				roles={roles}
				path="/facilities/:facilityId/monitor/activities"
				component={Activities}
			/>
			<PrivateRoute
				authed={authed}
				requiredRoles={['Support User', 'PCA Supervisor User']}
				roles={roles}
				path="/facilities/:facilityId/monitor/schedule"
				component={PlayYardSchedule}
			/>
			<PrivateRoute
				requiredRoles={['Support User', 'PCA Supervisor User']}
				authed={authed}
				roles={roles}
				path="/facilities/:facilityId/monitor/playgroups"
				component={Playgroups}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Support User', 'PCA Supervisor User']}
				path="/facilities/:facilityId/monitor/kennels"
				component={NightKennels}
			/>
			<PrivateRoute
				authed={authed}
				requiredRoles={['Support User', 'PCA Supervisor User']}
				roles={roles}
				path="/facilities/:facilityId/monitor/staffNotes"
				component={StaffNotes}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Support User', 'PCA Supervisor User']}
				path="/facilities/:facilityId/monitor/playgroup/:playgroupId"
				component={Playgroup}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Support User', 'PCA Supervisor User']}
				path="/facilities/:facilityId/monitor/unassigned"
				component={Unassigned}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User', 'Support User', 'PCA Supervisor User']}
				path="/facilities/:facilityId/monitor/dashboard"
				component={Dashboard}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/facilitySetup/basicInfo"
				component={FacilityBasicInfo}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/facilitySetup/memberships"
				component={FacilityMemberships}
			/>
			<Route
				exact
				path="/facilities/:facilityId/facilitySetup/marketing"
				render={({ match }) => (
					<Redirect to={`/facilities/${match.params.facilityId}/facilityMarketing/events`} />
				)}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/facilitySetup/petCareSetup"
				component={PetCareSetup}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/facilitySetup/availablePlaygroups"
				component={AvailablePlaygroups}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/facilitySetup/kennels"
				component={Kennels}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/facilitySetup/activitySetup"
				component={ActivitySetup}
			/>
			<Route
				exact
				path="/facilities/:facilityId/facilityMarketing"
				render={({ match }) => (
					<Redirect to={`/facilities/${match.params.facilityId}/facilityMarketing/events`} />
				)}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/facilityMarketing/:section"
				component={FacilityMarketing}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/facilitySetup/addons"
				component={AddonAvailability}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/facilityBooking/daycare/booking"
				component={Booking}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/facilityBooking/grooming/booking"
				component={Booking}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/facilityBooking/boarding/booking"
				component={Booking}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/facilityBooking/compatibility/booking"
				component={Booking}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/facilityBooking/daycare/cancellation"
				component={Cancellation}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/facilityBooking/boarding/cancellation"
				component={Cancellation}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/facilityBooking/grooming/cancellation"
				component={Cancellation}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/facilityBooking/compatibility/cancellation"
				component={Cancellation}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/facilitySetup/capacity"
				component={Capacity}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/facilitySetup/holidays"
				component={FacilityHolidays}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/facilitySetup/playgroups"
				component={Playgroups}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Support User', 'PCA Supervisor User']}
				exact
				path="/facilities/:facilityId/team"
				component={Team}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				exact
				path="/facilities/:facilityId/team/create"
				component={StaffCreate}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/team/:staffId"
				component={TeamMember}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/settings/services/availability"
				component={ServicesAvailability}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/settings/barPark/checkIn"
				component={BarParkSettings}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Support User', 'PCA Supervisor User']}
				path="/facilities/:facilityId/bar&park"
				component={BarAndPark}
			/>
			{/* Training - Facility Setup */}
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/training/setup/booking"
				component={TrainingBooking}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/training/setup/course_types"
				component={TrainingCourseTypes}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/training/setup/locations"
				component={TrainingLocations}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/training/setup/cancellation"
				component={TrainingCancellation}
			/>
			{/* End Training - Facility Setup */}
			{/* Training - Schedule */}
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/training/schedule"
				component={TrainingSchedule}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/training/calendar"
				component={TrainingCalendar}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/training/class/:classId/enrollment/:enrollmentId"
				component={TrainingEnrollment}
			/>
			<PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/training/class/:classId"
				component={TrainingClass}
			/>

			{/* <PrivateRoute
				authed={authed}
				roles={roles}
				requiredRoles={['Back Office User']}
				path="/facilities/:facilityId/training/calendar"
				component={() => <div>aa</div>}
			/> */}
			{/* end Training - Schedule */}
			<Redirect
				exact
				roles={roles}
				requiredRoles={['Back Office User']}
				from="/facilities/:facilityId/settings"
				to="/facilities/:facilityId/settings/services/availability"
			/>
			{/* <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/appointments/:appointmentId"
        component={AdminAppointmentDetailPage}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/scheduler"
        component={AdminScheduler}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/addstaff"
        component={AddStaffPage}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/staff/:staffId"
        component={EditStaffPage}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/stafflist"
        component={StaffListPage}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/clientlist"
        component={ClientListPage}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/addclient"
        component={AddClientPage}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        exact
        path="/client/:clientId"
        component={EditClientPage}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/client/:clientId/schedule"
        component={AdminScheduleAppointment}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/systemsettings"
        component={SystemSettingsPage}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/payments"
        component={AdminPaymentPage}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/map"
        component={MapComponent}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/staffData"
        component={StaffData}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/markets"
        component={Markets}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/playCare"
        component={PlayCare}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/apartments"
        component={Apartments}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/addapartment"
        component={AddApartment}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/editapartment/:apartmentId"
        component={EditApartment}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/market/:marketId"
        component={EditMarket}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/addmarket"
        component={AddMarket}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/editprices"
        component={EditPrices}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/vacation"
        component={Vacation}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/addpromo"
        component={AddPromo}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/promo/:promoId"
        component={EditPromo}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/partners"
        component={Partners}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/addpartner"
        component={AddPartner}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/editpartner/:partnerId"
        component={EditPartner}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        exact
        path="/facilities"
        component={FacilityList}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/facilities/:facilityId"
        component={EditFacility}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/vets"
        component={VetList}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/vet/:vetId"
        component={Vet}
      />
      <PrivateRoute
        authed={authed}
        loading={this.state.loading}
        path="/apps"
        component={AppVersions}
      /> */}
			{/*<PrivateRoute authed={authed} loading={this.state.loading} path='/newPets' component={NewPets} />*/}
		</Switch>
	)
}
